<template>
  <div class="add-plan-page">
    <!-- HEADER -->
    <div class="page-header">
      <div class="left">
        <button class="btn-back" @click="goBack">
          <i class="bx bx-arrow-back"></i>
        </button>
        <h2>Create Website Plans</h2>
      </div>
      <button class="btn-save" :disabled="isLoading" @click="saveAllPlans">
        <span v-if="isLoading" class="inline-spinner"></span>
        Save Plans
      </button>
    </div>

    <!-- CARD -->
    <div class="form-card">

      <!-- GLOBAL SELECTORS -->
      <div class="form-grid">

        <!-- WEBSITE SELECT -->
        <div class="form-group">
          <label>Select Website</label>
          <select v-model="selectedWebsite">
            <option disabled value="">-- Select Website --</option>
            <option v-for="w in websites" :key="w.website_id" :value="w.website_id">
              {{ w.website_domain }}
            </option>
          </select>
        </div>

        <!-- LAYOUT SELECT -->
        <div class="form-group">
          <label>Plan Layout</label>
          <select v-model="selectedLayout">
            <option disabled value="">-- Select Layout --</option>
            <option value="1">Single Plan</option>
            <option value="2">Two Plans</option>
            <option value="3">Three Plans</option>
          </select>
        </div>

      </div>

      <!-- MULTI-PLAN BUILDER -->
      <div
        v-if="selectedLayout"
        class="plans-builder"
        :class="'layout-' + selectedLayout"
        style="margin-top:20px; display:flex; gap:20px;"
      >

        <div
          v-for="(plan, index) in activePlanColumns"
          :key="index"
          class="single-plan-box"
          style="flex:1; border:1px solid var(--border); border-radius:var(--radius); padding:16px; background:var(--card);"
        >

          <h3 style="margin-bottom:14px; font-size:1rem; font-weight:700;">
            Plan {{ index + 1 }}
          </h3>

          <!-- TITLE -->
          <div class="form-group">
            <label>Plan Title</label>
            <input type="text" v-model="plan.title" placeholder="Enter plan title...">
          </div>

          <!-- PRICE -->
          <div class="form-group">
            <label>Price</label>
            <input type="number" v-model="plan.price" placeholder="Enter plan price...">
          </div>

          <!-- DURATION -->
          <div class="form-group">
            <label>Duration</label>
            <input type="text" v-model="plan.duration" placeholder="e.g. 30">
          </div>

          <!-- TYPE -->
          <div class="form-group">
            <label>Type</label>
            <select v-model="plan.type">
              <option value="Monthly">Monthly</option>
              <option value="Yearly">Yearly</option>
            </select>
          </div>

          <!-- CURRENCY -->
          <div class="form-group">
            <label>Currency</label>
            <select v-model="plan.curr">
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>

          <!-- STATUS -->
          <div class="form-group">
            <label>Status</label>
            <select v-model="plan.status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <!-- DESC -->
          <div class="form-group">
            <label>Description</label>
            <textarea rows="3" v-model="plan.desc" placeholder="Short description..."></textarea>
          </div>

          <!-- FEATURES -->
          <div class="form-group">
            <label>Features</label>

            <div class="features-list">

              <div
                v-for="(ft, fi) in plan.features"
                :key="fi"
                class="feature-item"
                style="display:flex; gap:6px; align-items:center;"
              >
                <input
                  type="text"
                  v-model="plan.features[fi]"
                  placeholder="Feature name..."
                >

                <button class="btn-delete-sm" type="button" @click="removeFeature(index, fi)">
                  <i class="bx bx-x"></i>
                </button>
              </div>

              <button class="btn-add-feature" type="button" @click="addFeature(index)">
                <i class="bx bx-plus"></i> Add Feature
              </button>

            </div>
          </div>

        </div>

      </div>

      <!-- SUBMIT FOOTER -->
      <div class="form-footer">
        <button type="button" class="btn-cancel" @click="goBack">Cancel</button>
        <button type="button" class="btn-create" @click="saveAllPlans">
          Save Plans
        </button>
      </div>

    </div>
  </div>
</template>


<script>
import {
  createWebsitePlan,
  getWebsitePlans
} from "@/apis/webplanapis";
import { getWebsites } from "@/apis/webapis";
import { useToast } from "vue-toastification";

export default {
  name: "AddWebPlan",

  data() {
    return {
      isLoading: false,

      // Dropdown State
      websites: [],
      selectedWebsite: "",
      selectedLayout: "",   // 1,2,3

      // Plan Forms (mapped to template)
      planForms: [
        {
          website_plan_id: "",
          website_plan_parent: "",
          title: "",
          price: "",
          duration: "",
          desc: "",
          type: "Monthly",
          type1:"",
          curr: "INR",
          status: "active",
          features: [""]
        },
        {
          website_plan_id: "",
          website_plan_parent: "",
          title: "",
          price: "",
          duration: "",
          desc: "",
          type: "Monthly",
          type1:"",
          curr: "INR",
          status: "active",
          features: [""]
        },
        {
          website_plan_id: "",
          website_plan_parent: "",
          title: "",
          price: "",
          duration: "",
          desc: "",
          type: "Monthly",
          type1:"",
          curr: "INR",
          status: "active",
          features: [""]
        }
      ],
    };
  },

  computed: {
    activePlanColumns() {
      if (!this.selectedLayout) return [];
      return this.planForms.slice(0, Number(this.selectedLayout));
    }
  },

  async mounted() {
    await this.fetchWebsites();
    if (this.$route.query.wid) {
    const wid = this.$route.query.wid;
    const ptype = this.$route.query.ptype;
      this.selectedWebsite = wid;
      this.selectedLayout = ptype;
    }
  },

  methods: {
    goBack() {
      this.$router.push("/pricing_tables");
    },

    async fetchWebsites() {
      const ldata = JSON.parse(sessionStorage.getItem("ldata"));
      try {
        const res = await getWebsites(ldata.user_id, "none");
        if (res.status) this.websites = res.records;
      } catch (err) {
        console.error(err);
      }
    },

    async loadExistingPlans() {
      if (!this.selectedWebsite) return;

      const ldata = JSON.parse(sessionStorage.getItem("ldata"));
      const res = await getWebsitePlans(
        ldata.user_id,
        this.selectedWebsite,
        "",
        "none"
      );

      if (!res.status || !res.records?.length) return;

      const existing = res.records.slice(0, 3);

      existing.forEach((p, i) => {
        this.planForms[i] = {
          ...this.planForms[i],
          website_plan_id: p.website_plan_id,
          website_plan_parent: p.website_plan_parent,
          title: p.website_plan_title,
          price: p.website_plan_price,
          duration: p.website_plan_duration,
          desc: p.website_plan_desc,
          type: p.website_plan_type,
          curr: p.website_plan_curr,
          status: p.website_plan_status,
          features: p.website_plan_features
            ? p.website_plan_features.split(",")
            : [""]
        };
      });
    },

    addFeature(i) {
      this.planForms[i].features.push("");
    },

    removeFeature(pi, fi) {
      if (this.planForms[pi].features.length > 1)
        this.planForms[pi].features.splice(fi, 1);
    },

    // SAVE ALL PLANS (1–3)
    async saveAllPlans() {
      const toast = useToast();

      if (!this.selectedWebsite)
        return toast.warning("Select website first");

      if (!this.selectedLayout)
        return toast.warning("Select layout first");

      const ldata = JSON.parse(sessionStorage.getItem("ldata"));

      this.isLoading = true;

      for (let p of this.activePlanColumns) {
        const fd = new FormData();

        fd.append("website_plan_parent", this.selectedWebsite);
        fd.append("website_plan_user", ldata.user_id);

        fd.append("website_plan_title", p.title);
        fd.append("website_plan_price", p.price);
        fd.append("website_plan_duration", p.duration);
        fd.append("website_plan_desc", p.desc);
        fd.append("website_plan_type", p.type);
        fd.append("website_plan_type1", this.selectedLayout);
        fd.append("website_plan_curr", p.curr);
        fd.append("website_plan_status", p.status);

        const features = p.features.filter(f => f.trim() !== "").join(",");
        fd.append("website_plan_features", features);

        if (p.website_plan_id)
          fd.append("website_plan_id", p.website_plan_id);

        await createWebsitePlan(fd);
      }

      this.isLoading = false;
      toast.success("Plans saved successfully");
      this.$router.push("/pricing_tables");
    }
  },

  watch: {
    selectedWebsite() {
      this.loadExistingPlans();
    }
  }
};
</script>



<style scoped>
/* Page Layout */
.add-plan-page {
  padding: 20px 24px;
  animation: fadeIn 0.3s ease;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}
.page-header .left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.page-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}
.btn-back {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
  cursor: pointer;
  transition: 0.3s;
}
.btn-back:hover {
  background: var(--primary);
  color: #fff;
}
.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-save:hover {
  opacity: 0.9;
}

/* Form */
.form-card {
  background: var(--card);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 24px;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px 16px;
}
.form-group {
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
}
label {
  font-size: 13px;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 6px;
}
input,
select,
textarea {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--card);
  color: var(--text);
  font-size: 13px;
  outline: none;
  transition: 0.3s border;
}
input:focus,
select:focus,
textarea:focus {
  border-color: var(--primary);
}
textarea {
  resize: none;
}

/* Image Preview */
.image-preview {
  margin-top: 8px;
  width: 90px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
}
.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Features */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}
.feature-item {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-add-feature {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef2ff;
  color: var(--primary);
  font-size: 13px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.btn-add-feature:hover {
  background: var(--primary);
  color: #fff;
}

/* Small Delete Button */
.btn-delete-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #fee2e2;
  color: #991b1b;
  cursor: pointer;
  transition: 0.2s;
}
.btn-delete-sm:hover {
  background: #dc2626;
  color: #fff;
}

/* Footer */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.btn-cancel {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-cancel:hover {
  background: #e5e7eb;
}
.btn-create {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-create:hover {
  opacity: 0.9;
}

/* Inline Spinner */
.inline-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

/* Animation */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1024px) {
  .add-plan-page {
    padding: 16px;
  }

  .page-header h2 {
    font-size: 1.15rem;
  }

  .form-card {
    padding: 20px;
  }

  .form-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px 14px;
  }

  input,
  select,
  textarea {
    font-size: 13px;
  }
}

/* 📱 Mobile (≤768px) */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .page-header .left {
    width: 100%;
    justify-content: space-between;
  }

  .page-header h2 {
    font-size: 1.05rem;
  }

  .btn-save {
    width: 100%;
    justify-content: center;
    padding: 10px 0;
  }

  .form-card {
    padding: 18px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .form-group label {
    font-size: 12.5px;
  }

  .form-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .btn-cancel,
  .btn-create {
    width: 100%;
    text-align: center;
    padding: 10px 0;
  }

  .image-preview {
    width: 100%;
    max-width: 200px;
    height: auto;
  }

  .features-list {
    gap: 6px;
  }

  .feature-item {
    flex-direction: row;
    gap: 6px;
  }

  .feature-item input {
    font-size: 13px;
  }

  .btn-add-feature {
    font-size: 12.5px;
    padding: 7px 10px;
  }

  .btn-delete-sm {
    width: 26px;
    height: 26px;
  }
}

/* 📲 Small Mobile (≤480px) */
@media (max-width: 480px) {
  .add-plan-page {
    padding: 14px;
  }

  .page-header {
    margin-bottom: 14px;
  }

  .page-header .left {
    gap: 8px;
  }

  .btn-back {
    width: 32px;
    height: 32px;
  }

  .page-header h2 {
    font-size: 1rem;
  }

  .form-card {
    padding: 16px;
    border-radius: 10px;
  }

  label {
    font-size: 12px;
  }

  input,
  select,
  textarea {
    font-size: 12.5px;
    padding: 7px 9px;
  }

  .btn-add-feature {
    width: 100%;
    justify-content: center;
  }

  .form-footer {
    margin-top: 16px;
  }

  .btn-cancel,
  .btn-create {
    font-size: 13px;
    padding: 10px 0;
  }

  .inline-spinner {
    width: 14px;
    height: 14px;
  }

  .feature-item {
    flex-direction: row;
    align-items: center;
  }

  .btn-delete-sm {
    width: 24px;
    height: 24px;
  }

  textarea {
    min-height: 80px;
  }
}

/* 🧾 Ultra Small Devices (≤380px) */
@media (max-width: 380px) {
  .page-header h2 {
    font-size: 0.95rem;
  }

  .form-card {
    padding: 12px;
  }

  input,
  select,
  textarea {
    font-size: 12px;
  }

  .btn-add-feature {
    font-size: 12px;
    padding: 6px 8px;
  }

  .btn-cancel,
  .btn-create {
    font-size: 12.5px;
  }
}
/* ============================================
   ADD PLAN PAGE — LAYOUT + MULTI-PLAN SUPPORT
   ============================================ */

.add-plan-page {
  padding: 22px 24px;
  animation: fadeIn .25s ease;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header .left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-header h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}

.btn-back {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: .25s;
  color: var(--text);
}
.btn-back:hover {
  background: var(--primary);
  color: #fff;
}

.btn-save {
  background: var(--primary);
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: .25s;
}
.btn-save:disabled {
  opacity: .6;
}
.btn-save:hover {
  opacity: 0.9;
}

/* Main Card */
.form-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 22px;
  box-shadow: 0 2px 8px rgba(0,0,0,.05);
}

/* Grid for Website + Layout */
.layout-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

.form-group {
  display: flex;
  flex-direction: column;
}
.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 6px;
}

input,
select,
textarea {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--text);
  outline: none;
  transition: .25s ease;
}
input:focus,
select:focus,
textarea:focus {
  border-color: var(--primary);
}

/* MULTI-PLAN FORM GRID */
.multi-plan-wrapper {
  display: grid;
  grid-template-columns: repeat(var(--cols), 1fr);
  gap: 20px;
  margin-top: 20px;
}

.plan-column {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px;
  box-shadow: 0 1px 6px rgba(0,0,0,.05);
}

.plan-column h3 {
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Features Box */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}
.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-add-feature {
  background: #eef2ff;
  color: var(--primary);
  border: none;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  transition: .25s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}
.btn-add-feature:hover {
  background: var(--primary);
  color: #fff;
}

.btn-delete-sm {
  background: #fee2e2;
  color: #991b1b;
  border: none;
  min-width: 28px;
  min-height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: .25s ease;
}
.btn-delete-sm:hover {
  background: #dc2626;
  color: #fff;
}

/* Image Preview */
.image-preview {
  margin-top: 8px;
  width: 100%;
  max-width: 140px;
  border-radius: 10px;
  border: 1px solid var(--border);
  overflow: hidden;
}
.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Footer */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.btn-cancel {
  background: #f3f4f6;
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: .25s;
}
.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-create {
  background: var(--primary);
  color: #fff;
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: .25s;
}
.btn-create:hover {
  opacity: .9;
}

/* Spinner */
.inline-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

/* ============================
   RESPONSIVE
   ============================ */

@media (max-width: 1024px) {
  .multi-plan-wrapper {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .multi-plan-wrapper {
    grid-template-columns: 1fr !important;
  }
  .btn-save {
    width: 100%;
    justify-content: center;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-create {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .add-plan-page {
    padding: 14px;
  }
  .page-header h2 {
    font-size: 1.05rem;
  }
  .plan-column {
    padding: 14px;
  }
  .btn-add-feature {
    width: 100%;
    justify-content: center;
  }
}

/* Fade Animation */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

</style>

